import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    List,
    ListItem,
    ListItemText,
    Typography,
    Box,
    Paper,
    Skeleton
} from '@mui/material';
// import NoteIcon from '@mui/icons-material/Note';
import moment from 'moment';
import { useLazyGetNotesQuery, useCreateNoteMutation } from '../../../_api/noteApi';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import { Chip } from '@mui/material';

const NoteDialog = ({ open, onClose, target, targetType, name }) => {
    const { t } = useTranslation()

    // State for dialog and form
    // const [open, setOpen] = useState(false);
    const [noteText, setNoteText] = useState('');

    // Pagination state
    const [page, setPage] = useState(1);
    const [notes, setNotes] = useState([]);
    const [hasNextPage, setHasNextPage] = useState(true);

    // API hooks
    const [getNotes] = useLazyGetNotesQuery();
    const [createNote, { isLoading: isSubmitting, isSuccess, error , reset }] = useCreateNoteMutation();

    const [loading, setLoading] = useState(true);

    // Load notes with pagination
    const loadNotes = useCallback(async (pageNum = 1) => {
        try {
            setLoading(true);
            const result = await getNotes({
                target,
                page: pageNum,
                //limit: 5,
            }).unwrap();

            if (pageNum === 1) {
                setNotes([...result?.docs]);
            } else {
                setNotes(prev => [...prev, ...result?.docs]);
            }

            // Check if we've reached the end of available notes
            setHasNextPage(result.hasNextPage);
        } catch (error) {
            console.error('Failed to load notes:', error);
            setHasNextPage(false);
        }finally {
            setLoading(false);
        }
    }, [getNotes, target]);

    // Initial load when dialog opens
    useEffect(() => {
        if (open && target) {
            setPage(1);
            loadNotes(1);
        }
    }, [open, target, loadNotes]);
  
    useEffect(() => {
            if (!open) {
               reset()
            }
        }, [open]);
    
    // Handle loading more
    const loadMoreNotes = useCallback(() => {
        if (!loading && hasNextPage) {
            const nextPage = page + 1;
            setPage(nextPage);
            loadNotes(nextPage);
        }
    }, [loading, hasNextPage, page, loadNotes]);

    // Set up infinite scroll hook
    const [infiniteScrollRef] = useInfiniteScroll({
        loading: loading,
        hasNextPage,
        onLoadMore: loadMoreNotes,
        // Only trigger when component is visible and not initially loading
        disabled: !open || loading,
        // Root margin to trigger loading before reaching the end
        rootMargin: '0px 0px 200px 0px',
    });

    // Handlers
    // const handleOpenDialog = () => {
    //     setOpen(true);
    // };

    const handleCloseDialog = () => {
        onClose();
        setNoteText('');
        setNotes([]);
        setPage(1);
        setHasNextPage(true);
    };

    const handleSubmitNote = async (e) => {
        e.preventDefault();
        if (!noteText.trim()) return;

        try {
            await createNote({
                text: noteText,
                target,
                targetType
            }).unwrap();

            // Reset form and refresh notes
            setNoteText('');
            setPage(1);
            loadNotes(1);

            setTimeout(() => {
                handleCloseDialog();
            }, 2000);
        } catch (error) {
            console.error('Failed to create note:', error);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleCloseDialog}
            fullWidth
            maxWidth="md"
            disableRestoreFocus
            disableScrollLock
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {t("Notes")}
                    {name && (
                        <Chip
                            label={name}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                    )}
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Paper variant="outlined" sx={{ maxHeight: "50vh", overflow: 'auto', mb: 3, p: 1 }}>
                    {loading && page === 1 ? (
                        <List>
                            {[...Array(3)].map((_, index) => (
                                <ListItem key={`skeleton-${index}`} divider={index < 2}>
                                    <ListItemText
                                        primary={<Skeleton animation="wave" height={20} width="80%" />}
                                        secondary={
                                            <Box sx={{ mt: 1 }}>
                                                <Skeleton animation="wave" height={15} width="40%" sx={{ display: 'inline-block' }} />
                                                <Skeleton animation="wave" height={15} width="35%" sx={{ display: 'inline-block', ml: 1 }} />
                                            </Box>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    ) : notes?.length > 0 ? (
                        <List>
                            {notes.map((note, index) => (
                                <ListItem key={note.id} divider={index < notes.length - 1}>
                                    <ListItemText
                                        primary={note.text}
                                        secondary={
                                            <React.Fragment>
                                                <Typography
                                                    component="span"
                                                    variant="body2"
                                                    color="text.primary"
                                                >
                                                    {note?.user?.name || "Anonymous"}
                                                </Typography>
                                                {" - "}
                                                {note.createdAt && moment(note.createdAt).format('DD/MM/YYYY - hh:mm A')}
                                            </React.Fragment>
                                        }
                                    />
                                </ListItem>
                            ))}
                            {(loading || hasNextPage) && (
                                <Box ref={infiniteScrollRef}>
                                    {[...Array(2)].map((_, index) => (
                                        <ListItem key={`load-more-skeleton-${index}`} divider={index < 1}>
                                            <ListItemText
                                                primary={<Skeleton animation="wave" height={20} width="80%" />}
                                                secondary={
                                                    <Box sx={{ mt: 1 }}>
                                                        <Skeleton animation="wave" height={15} width="20%" sx={{ display: 'inline-block' }} />
                                                        <Skeleton animation="wave" height={15} width="13%" sx={{ display: 'inline-block', ml: 1 }} />
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </Box>
                            )}
                        </List>
                    ) : (
                        <Typography align="center" py={2} color="text.secondary">
                            {t("No notes found.")}
                        </Typography>
                    )}
                </Paper>

                {/* Create Note Section */}
                <Typography variant="h6" gutterBottom>
                    {t("Add Note")}
                </Typography>

                <Box onSubmit={handleSubmitNote}>
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        placeholder={t("Add Note")}
                        variant="outlined"
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        disabled={isSubmitting}
                        sx={{ mb: 2 }}
                    />
                </Box>
            </DialogContent>

            <DialogActions>
                {isSuccess && <Typography color="success.main">{t("Done successfully")}</Typography>}
                {!!error && <Typography color="error">{error}</Typography>}
                <Box mr="auto" />
                <LoadingButton
                    onClick={handleCloseDialog}
                    variant="contained"
                    sx={{ textTransform: "none" }}
                >
                    {t("Cancel")}
                </LoadingButton>
                <LoadingButton
                    onClick={handleSubmitNote}
                    variant="contained"
                    sx={{ textTransform: "none" }}
                    disabled={isSubmitting || !noteText.trim()}
                >
                    {t("Confirm")}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default NoteDialog;
